/**
 * Enumerations shared across the whole platform. These MUST stay in sync with:
 *  - the GraphQL schema (packages/graphql-schema)
 *  - Strapi content-type enum options (apps/strapi)
 *  - PostgreSQL enum/check constraints (infrastructure/postgres)
 * They are the canonical TypeScript source of truth.
 */

/** Supported content/UI languages. */
export const LOCALES = ["pl", "en"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "pl";

/** Concrete rendered theme. */
export type ThemeMode = "light" | "dark";

/** User theme *preference* (system resolves to a ThemeMode at runtime). */
export type ThemePreference = ThemeMode | "system";
export const THEME_PREFERENCES: readonly ThemePreference[] = ["light", "dark", "system"];

/** Which frontend build is active in the shell. */
export type FrontendVersion = "v1" | "v2";
export const FRONTEND_VERSIONS: readonly FrontendVersion[] = ["v1", "v2"];

/** Draft/publish lifecycle mirrored from Strapi. */
export type PublicationStatus = "draft" | "published" | "archived";

/** Sources a lead can originate from (drives routing + scoring). */
export const LEAD_SOURCES = [
	"contact_form",
	"quick_sales_contact",
	"quote_request",
	"report_download",
	"case_study_download",
	"webinar",
	"newsletter",
	"landing_page",
	"campaign",
	"ai_assistant_cta",
	"readiness_audit",
] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

/** Lifecycle of a captured lead (handover to CRM later). */
export const LEAD_STATUSES = [
	"new",
	"deduplicated",
	"qualified",
	"routed",
	"contacted",
	"won",
	"lost",
	"spam",
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** Content types that are searchable / indexable in Elasticsearch. */
export const SEARCHABLE_CONTENT_TYPES = [
	"service",
	"company",
	"case_study",
	"article",
	"resource",
	"landing_page",
	"webinar",
	"page",
] as const;
export type SearchableContentType = (typeof SEARCHABLE_CONTENT_TYPES)[number];

/** Consent categories captured with every lead (RODO/GDPR). */
export const CONSENT_KINDS = [
	"privacy_policy",
	"marketing_email",
	"marketing_phone",
	"data_processing",
	"profiling",
] as const;
export type ConsentKind = (typeof CONSENT_KINDS)[number];

/** Roles mirrored from Keycloak → mapped to Strapi & GraphQL authorization. */
export const ROLES = [
	"group_admin",
	"domain_admin",
	"domain_editor",
	"marketing",
	"sales",
	"analyst",
	"content_approver",
	"translator",
] as const;
export type Role = (typeof ROLES)[number];
